import { API_BASE } from '../apiBase';
import React, { useEffect, useState, useRef, useMemo } from 'react';
import axios from 'axios';
import ItemPopup from './components/ItemPopup';
import Scanner from './components/Scanner';
import { useNavigate } from 'react-router-dom';

// Item image with automatic retry. Dropbox throttles bursts of shared-link
// requests (a grid fires 15-30 at once), and a failed <img> never retries by
// itself — so throttled tiles stayed broken forever. This retries twice with a
// staggered delay (a cache-busting param forces the re-request), then falls
// back to the placeholder.
const ITEM_PLACEHOLDER = 'https://placehold.co/100x100?text=No+Image';
function ItemImg({ src, alt, style }) {
  const [attempt, setAttempt] = useState(0);
  const [failed, setFailed] = useState(false);
  useEffect(() => { setAttempt(0); setFailed(false); }, [src]);
  const url = !src
    ? ITEM_PLACEHOLDER
    : attempt === 0 ? src : `${src}${src.includes('?') ? '&' : '?'}r=${attempt}`;
  const handleError = () => {
    if (!src || failed) return;
    if (attempt >= 2) { setFailed(true); return; }
    // Staggered + jittered so 20 failed tiles don't re-burst in unison.
    const delay = 1200 * (attempt + 1) + Math.random() * 1000;
    setTimeout(() => setAttempt((a) => a + 1), delay);
  };
  return (
    <img
      src={failed ? ITEM_PLACEHOLDER : url}
      alt={alt}
      loading="lazy"
      onError={handleError}
      style={style}
    />
  );
}

// Location input with recency-based suggestions. Shows the 3 most recently
// USED locations (from existing items) — and as you type, the 3 most recent
// that START with what you've typed. "2" → your last three rack-2 spots.
function LocationInput({ value, onChange, locations, placeholder, inputStyle, dropUp }) {
  const [open, setOpen] = useState(false);
  const typed = String(value || '').trim().toLowerCase();
  const matches = locations
    .filter((l) => !typed || l.toLowerCase().startsWith(typed))
    .slice(0, 3);
  const showList = open && matches.length > 0 && !(matches.length === 1 && matches[0].toLowerCase() === typed);
  return (
    <div style={{ position: 'relative', flex: 1, minWidth: 0 }}>
      <input
        type="text"
        value={value || ''}
        onChange={(e) => { onChange(e.target.value); setOpen(true); }}
        onFocus={() => setOpen(true)}
        onBlur={() => setTimeout(() => setOpen(false), 150)}
        placeholder={placeholder}
        style={{ width: '100%', boxSizing: 'border-box', ...inputStyle }}
      />
      {showList && (
        <div style={{
          position: 'absolute', left: 0, right: 0, zIndex: 50,
          ...(dropUp ? { bottom: '100%', marginBottom: '4px' } : { top: '100%', marginTop: '4px' }),
          background: 'white', border: '1px solid #ccc', borderRadius: '8px',
          boxShadow: '0 4px 14px rgba(0,0,0,0.15)', overflow: 'hidden',
        }}>
          {matches.map((l) => (
            <button
              key={l}
              type="button"
              onMouseDown={(e) => { e.preventDefault(); onChange(l); setOpen(false); }}
              style={{
                display: 'block', width: '100%', textAlign: 'left', padding: '12px 14px',
                border: 'none', background: 'white', cursor: 'pointer', fontSize: '1rem',
                borderBottom: '1px solid #f0f0f0',
              }}
            >
              📍 {l}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

// "5 of 6" for returnable items (6 owned, 1 checked out); plain count otherwise.
const stockText = (item) =>
  item && item.returnable && item.ownedQuantity != null
    ? `${item.quantity} of ${item.ownedQuantity}`
    : `${item?.quantity ?? 0}`;

function Inventory() {
  const [items, setItems] = useState([]);
  const [filteredItems, setFilteredItems] = useState([]);
  const [cart, setCart] = useState(JSON.parse(localStorage.getItem('cart')) || []);
  const [popupType, setPopupType] = useState('none'); // 'none', 'item', or 'add'
  const [selectedItem, setSelectedItem] = useState(null);
  const [newItem, setNewItem] = useState({});
  const [visibleCount, setVisibleCount] = useState(15);
  const [searchTerm, setSearchTerm] = useState('');
  // View + sort preferences (remembered per device). Grid is the phone default —
  // it fits ~3x more items per screen than the list.
  const [viewMode, setViewMode] = useState(() =>
    localStorage.getItem('invViewMode') || (window.innerWidth < 640 ? 'grid' : 'list'));
  const [sortBy, setSortBy] = useState(() => localStorage.getItem('invSortBy') || 'name');
  const [isMobile, setIsMobile] = useState(window.innerWidth < 640);
  const [uploadingAddImg, setUploadingAddImg] = useState(false);
  const addFileRef = useRef(null);
  // Multi-select + bulk location move
  const [selectMode, setSelectMode] = useState(false);
  const [selectedIds, setSelectedIds] = useState([]);
  const [bulkLoc, setBulkLoc] = useState('');
  const [bulkSaving, setBulkSaving] = useState(false);
  const sentinelRef = useRef(null);
  const navigate = useNavigate();
  const role = localStorage.getItem('role') || '';
  const popupRef = useRef(null);

  useEffect(() => {
    const onResize = () => setIsMobile(window.innerWidth < 640);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);
  useEffect(() => { localStorage.setItem('invViewMode', viewMode); }, [viewMode]);
  useEffect(() => { localStorage.setItem('invSortBy', sortBy); }, [sortBy]);

  // Freeze the page while the item card is open. Without this, iOS Safari
  // scrolls the GRID behind the overlay instead of the card's own content.
  // (position:fixed on <body> is the only lock iOS reliably respects; the
  // scroll position is stashed and restored on close.)
  useEffect(() => {
    if (popupType === 'none') return;
    const y = window.scrollY;
    const b = document.body.style;
    b.position = 'fixed';
    b.top = `-${y}px`;
    b.left = '0';
    b.right = '0';
    b.width = '100%';
    b.overflow = 'hidden';
    return () => {
      b.position = ''; b.top = ''; b.left = ''; b.right = ''; b.width = ''; b.overflow = '';
      window.scrollTo(0, y);
    };
  }, [popupType]);

  // Look a scanned code up against names + aliases (barcodes are stored as
  // aliases). Match -> show the +/- quantity card; no match -> offer Add New Item
  // with the code pre-filled as an alias. Shared by the in-page Scanner and by an
  // incoming scan handed over from the global Scanner (via router state).
  const handleScannedCode = (code) => {
    if (!code) return;
    setSearchTerm(code);
    const lower = String(code).toLowerCase();
    const matches = items.filter((item) =>
      item.name?.toLowerCase().includes(lower) ||
      item.location?.toLowerCase().includes(lower) ||
      (Array.isArray(item.aliases) && item.aliases.some((a) => a.toLowerCase().includes(lower)))
    );
    if (matches.length > 0) {
      const exact = matches.find((item) =>
        item.name?.toLowerCase() === lower ||
        (Array.isArray(item.aliases) && item.aliases.some((a) => a.toLowerCase() === lower))
      );
      setScanQty(0);
      setScanHit(exact || matches[0]);
    } else if (['admin', 'superadmin'].includes(role)) {
      if (window.confirm(`"${code}" isn't in your inventory yet.\n\nAdd it as a new item?`)) {
        setNewItem({ aliases: [code], aliasesText: code });
        setPopupType('add');
      }
    } else {
      alert(`"${code}" isn't in your inventory.`);
    }
  };

  // A scan handed over from the global Scanner arrives via sessionStorage (set
  // just before a full-page navigation here). Process it once the inventory has
  // loaded, then clear it so a refresh doesn't re-trigger it.
  const incomingHandledRef = useRef(false);
  useEffect(() => {
    if (incomingHandledRef.current || !items.length) return;
    let code = null;
    try { code = sessionStorage.getItem('pendingInventoryScan'); } catch (_) {}
    if (!code) return;
    incomingHandledRef.current = true;
    try { sessionStorage.removeItem('pendingInventoryScan'); } catch (_) {}
    handleScannedCode(code);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [items]);

  // Fetch items + token/role validation
  useEffect(() => {
    const token = localStorage.getItem('token');
    if (!token) {
      navigate('/login');
      return;
    }

    const fetchData = async () => {
      try {
        const validateRes = await axios.get(`${API_BASE}/validate-token`, {
          headers: { Authorization: token },
        });

        localStorage.setItem('role', validateRes.data.role);
        localStorage.setItem('name', validateRes.data.name);
        localStorage.setItem('hasInventoryAccess', validateRes.data.hasInventoryAccess.toString());

        if (!validateRes.data.hasInventoryAccess) {
          alert('You do not have access to the inventory.');
          navigate('/login');
          return;
        }

        const itemsRes = await axios.get(`${API_BASE}/items`, {
          headers: { Authorization: token },
        });

        console.log('Items fetched successfully:', itemsRes.data);

        const fetchedItems = Array.isArray(itemsRes.data) ? itemsRes.data : [];
        setItems(fetchedItems);
        setFilteredItems(fetchedItems);
      } catch (err) {
        console.error('Error in inventory load:', err.response || err);
        if (err.response?.status === 401) {
          localStorage.clear();
          navigate('/login');
        } else {
          setItems([]);
          setFilteredItems([]);
        }
      }
    };

    fetchData();
  }, [navigate]);

  // Live search filtering
  useEffect(() => {
    if (searchTerm.trim() === '') {
      setFilteredItems(items);
    } else {
      const filtered = items.filter((item) =>
        item.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        item.location?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        (Array.isArray(item.aliases) && item.aliases.some((a) => a.toLowerCase().includes(searchTerm.toLowerCase())))
      );
      setFilteredItems(filtered);
    }
  }, [searchTerm, items]);

  // Sync cart to localStorage
  useEffect(() => {
    localStorage.setItem('cart', JSON.stringify(cart));
  }, [cart]);

  // ---- Sorting ----
  // "Recently added/edited" uses the timestamps the server now records; items
  // created before timestamps existed fall back to the creation time embedded
  // in their Mongo _id, so old items still sort sensibly.
  const createdTs = (it) => it.createdAt
    ? new Date(it.createdAt).getTime()
    : parseInt(String(it._id).slice(0, 8), 16) * 1000;
  const editedTs = (it) => it.updatedAt ? new Date(it.updatedAt).getTime() : createdTs(it);
  const sortedItems = useMemo(() => {
    const arr = [...filteredItems];
    if (sortBy === 'newest') arr.sort((a, b) => createdTs(b) - createdTs(a));
    else if (sortBy === 'edited') arr.sort((a, b) => editedTs(b) - editedTs(a));
    else arr.sort((a, b) => String(a.name || '').localeCompare(String(b.name || ''), undefined, { numeric: true }));
    return arr;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filteredItems, sortBy]);

  // Locations ranked by how recently anything was stored/edited there — feeds
  // the suggestion dropdowns (add form + bulk move).
  const recentLocations = useMemo(() => {
    const byLoc = new Map();
    for (const it of items) {
      const loc = String(it.location || '').trim();
      if (!loc) continue;
      const ts = it.updatedAt ? new Date(it.updatedAt).getTime() : createdTs(it);
      if (!byLoc.has(loc) || byLoc.get(loc) < ts) byLoc.set(loc, ts);
    }
    return [...byLoc.entries()].sort((a, b) => b[1] - a[1]).map(([l]) => l);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [items]);

  const toggleSelected = (id) => {
    setSelectedIds((prev) => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);
  };

  // Move every selected item to one new location in a single action.
  const bulkMove = async () => {
    const loc = bulkLoc.trim();
    if (!loc || !selectedIds.length || bulkSaving) return;
    setBulkSaving(true);
    try {
      const token = localStorage.getItem('token');
      const updated = await Promise.all(selectedIds.map((id) =>
        axios.put(`${API_BASE}/items/${id}`, { location: loc }, { headers: { Authorization: token } })
          .then((r) => r.data)
      ));
      const patch = (prev) => prev.map((i) => updated.find((u) => u && u._id === i._id) || i);
      setItems(patch);
      setFilteredItems(patch);
      setSelectMode(false);
      setSelectedIds([]);
      setBulkLoc('');
    } catch (err) {
      console.error('Bulk move failed:', err);
      alert('Could not move some items — check and try again.');
    } finally {
      setBulkSaving(false);
    }
  };

  const handleInputChange = (e) => setSearchTerm(e.target.value);

  // ---- Infinite scroll ----
  // No more "Show More" button: when the invisible sentinel under the list
  // scrolls into view, load the next page automatically.
  useEffect(() => { setVisibleCount(15); }, [searchTerm, sortBy, viewMode]);
  useEffect(() => {
    const el = sentinelRef.current;
    if (!el) return;
    const obs = new IntersectionObserver((entries) => {
      if (entries[0].isIntersecting) {
        setVisibleCount((prev) => (prev < sortedItems.length ? prev + 15 : prev));
      }
    }, { rootMargin: '600px' });
    obs.observe(el);
    return () => obs.disconnect();
    // visibleCount in the deps re-arms the observer after each batch — otherwise
    // a sentinel that STAYS in view never fires again and the list stalls with
    // "Loading more…" showing.
  }, [sortedItems.length, visibleCount]);

  const handleItemClick = (item) => {
    setSelectedItem(item);
    setPopupType('item');
  };

  const handleAddItemClick = () => {
    setNewItem({});
    setPopupType('add');
    setSelectedItem(null);
  };

  // ---- Super-admin quick delete ----
  // Right-click (desktop) or tap-and-hold 2s (mobile) reveals a Delete button on
  // an item row. Super Admin only.
  const isSuper = role === 'superadmin';
  const [deleteTargetId, setDeleteTargetId] = useState(null);

  const handleDeleteItem = async (item) => {
    if (!window.confirm(`Delete "${item.name}" from inventory? This can't be undone.`)) return;
    try {
      await axios.delete(`${API_BASE}/items/${item._id}`, {
        headers: { Authorization: localStorage.getItem('token') },
      });
      setItems((prev) => prev.filter((i) => i._id !== item._id));
      setFilteredItems((prev) => prev.filter((i) => i._id !== item._id));
      setDeleteTargetId(null);
      // If the deleted item's card is open, close it.
      if (selectedItem && selectedItem._id === item._id) closePopup();
    } catch (err) {
      console.error('Delete item failed:', err);
      alert('Could not delete the item.');
    }
  };

  // (Long-press-to-delete on mobile was removed — it fought with scrolling and
  // select mode. Deleting now happens via the Delete button on the item card,
  // or right-click on desktop.)

  // ---- Post-scan quick actions ----
  // When a scan matches an item, this card offers instant withdraw/deposit
  // (through the same cart flow as ItemPopup, so checkout logging still
  // applies) or jumps to the full item card.
  const [scanHit, setScanHit] = useState(null);
  const [scanQty, setScanQty] = useState(1);

  // scanQty is a SIGNED counter: −5 means "take 5", +3 means "return 3".
  const scanQuickSubmit = () => {
    if (!scanQty) return;
    setCart([
      ...cart,
      {
        itemId: scanHit._id,
        name: scanHit.name,
        location: scanHit.location,
        action: scanQty < 0 ? 'withdraw' : 'deposit',
        quantity: Math.abs(scanQty),
        price: scanHit.price,
      },
    ]);
    setScanHit(null);
    setScanQty(0);
  };

  const closePopup = () => {
    setPopupType('none');
    setSelectedItem(null);
    setNewItem({});
  };

  const updateItem = (updatedItem) => {
    setItems((prev) => prev.map((i) => (i._id === updatedItem._id ? updatedItem : i)));
    setFilteredItems((prev) => prev.map((i) => (i._id === updatedItem._id ? updatedItem : i)));
    closePopup();
  };

  // Guard against double-submits: a fast double-tap on "Create Item" fired two
  // POSTs and made duplicate items. Locked while the first request is in flight.
  // keepOpen = "Create + Add Another": saves, then resets the form but KEEPS the
  // location — because the next item usually goes in the same bin.
  const [isCreating, setIsCreating] = useState(false);
  const handleCreateItem = async (e, keepOpen = false) => {
    if (e) e.preventDefault();
    if (isCreating) return;
    if (!(newItem.name || '').trim()) { alert('Item name is required.'); return; }
    setIsCreating(true);
    try {
      // Slang names are kept as RAW TEXT while typing (so spaces survive —
      // "pipe bender, hickey") and only split into a list here at save time.
      const payload = { ...newItem };
      if (payload.aliasesText != null) {
        payload.aliases = payload.aliasesText.split(',').map((a) => a.trim()).filter(Boolean);
      }
      delete payload.aliasesText;
      const res = await axios.post(`${API_BASE}/items`, payload, {
        headers: { Authorization: localStorage.getItem('token') },
      });
      setItems((prev) => [...prev, res.data]);
      setFilteredItems((prev) => [...prev, res.data]);
      if (keepOpen) {
        setNewItem({ location: newItem.location || '' }); // same bin, next item
      } else {
        closePopup();
      }
    } catch (err) {
      console.error('Create item error:', err);
      alert('Could not create the item.');
    } finally {
      setIsCreating(false);
    }
  };

  const placeholderImage = 'https://placehold.co/100x100?text=No+Image';

  // INSTANT thumbnails: each item carries a tiny embedded thumb (data-URL)
  // inside the list response — zero image requests, nothing to load. Items
  // that haven't been backfilled yet fall back to the backend image proxy.
  const itemImgSrc = (item) =>
    item.thumb
      ? item.thumb
      : item.image ? `${API_BASE}/items/${item._id}/image?v=${new Date(item.updatedAt || 0).getTime()}` : null;

  return (
    <div className="min-h-screen bg-gray-100 text-gray-900">
      <div style={{ padding: '24px', maxWidth: '1400px', margin: '0 auto' }}>
      <h1 className="text-3xl font-bold tracking-tight font-sans text-gray-900 mb-8">
        Shop Inventory
      </h1>

        {/* Controls Row - Search + Buttons */}
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '15px',
            marginBottom: '24px',
            flexWrap: 'wrap',
          }}
        >
          {/* Search Input — with a one-tap ✕ to clear (handy after a scan
              fills the box with a long barcode). */}
          <div style={{ position: 'relative', flexGrow: 1, minWidth: '200px' }}>
            <input
              type="text"
              placeholder="Search items by name, location, or alias..."
              value={searchTerm}
              onChange={handleInputChange}
              style={{
                padding: '10px 40px 10px 14px',
                borderRadius: '6px',
                border: '1px solid #ccc',
                width: '100%',
                boxSizing: 'border-box',
                fontSize: '1rem',
              }}
            />
            {searchTerm && (
              <button
                onClick={() => setSearchTerm('')}
                title="Clear search"
                aria-label="Clear search"
                style={{
                  position: 'absolute', right: '8px', top: '50%', transform: 'translateY(-50%)',
                  width: '26px', height: '26px', borderRadius: '50%', border: 'none',
                  background: '#d1d5db', color: '#444', cursor: 'pointer',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  fontSize: '14px', lineHeight: 1, padding: 0,
                }}
              >
                ✕
              </button>
            )}
          </div>

          {/* Camera barcode/QR scan — fills the search box with the scanned
              code. If nothing matches, offers to create the item with the
              code saved as an alias so the NEXT scan finds it instantly. */}
          <Scanner onScan={handleScannedCode} />

          {/* Action Buttons */}
          {['admin', 'superadmin'].includes(role) && (
            <>
              <button
                onClick={() => navigate('/inventory/logs')}
                style={{
                  padding: '10px 16px',
                  backgroundColor: '#6c757d',
                  color: 'white',
                  border: 'none',
                  borderRadius: '6px',
                  cursor: 'pointer',
                }}
              >
                View Logs
              </button>
              <button
                onClick={handleAddItemClick}
                style={{
                  padding: '10px 16px',
                  backgroundColor: '#28a745',
                  color: 'white',
                  border: 'none',
                  borderRadius: '6px',
                  cursor: 'pointer',
                  fontWeight: 'bold',
                }}
              >
                + Add Item
              </button>
            </>
          )}

          <button
  onClick={() => navigate('/inventory/cart')}
  style={{
    padding: '10px 16px',
    backgroundColor: '#007bff',
    color: 'white',
    border: 'none',
    borderRadius: '6px',
    cursor: 'pointer',
    fontWeight: 'bold',
  }}
>
  Cart ({cart.length})
</button>
        </div>

        {/* View controls — sort + layout on their own row. Compact on phones so
            Select / A–Z / Newest / Edited / grid / list all fit without
            squishing or wrapping mid-label. */}
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: isMobile ? 'flex-start' : 'space-between',
            gap: isMobile ? '6px' : '12px',
            marginBottom: '20px',
            flexWrap: 'wrap',
          }}
        >
          {/* Select mode: tap items to build a group, then move them all at once */}
          <button
            onClick={() => {
              setSelectMode((m) => !m);
              setSelectedIds([]);
              setBulkLoc('');
            }}
            style={{
              padding: isMobile ? '8px 10px' : '10px 16px',
              borderRadius: '6px', cursor: 'pointer', whiteSpace: 'nowrap',
              fontSize: isMobile ? '0.78rem' : '0.9rem', fontWeight: 600,
              border: selectMode ? '1px solid #007bff' : '1px solid #ccc',
              backgroundColor: selectMode ? '#007bff' : 'white',
              color: selectMode ? 'white' : '#555',
            }}
          >
            {selectMode ? `✓ ${selectedIds.length}` : 'Select'}
          </button>

          {/* Segmented sort control — matches the grid/list toggle styling */}
          <div style={{ display: 'flex', borderRadius: '6px', overflow: 'hidden', border: '1px solid #ccc' }}>
            {[
              { key: 'name', label: 'A–Z' },
              { key: 'newest', label: 'Newest' },
              { key: 'edited', label: 'Edited' },
            ].map((opt) => (
              <button
                key={opt.key}
                onClick={() => setSortBy(opt.key)}
                title={opt.key === 'name' ? 'Alphabetical' : opt.key === 'newest' ? 'Recently added first' : 'Recently edited first'}
                style={{
                  padding: isMobile ? '8px 9px' : '10px 16px',
                  border: 'none', cursor: 'pointer', whiteSpace: 'nowrap',
                  fontSize: isMobile ? '0.78rem' : '0.9rem', fontWeight: 600,
                  backgroundColor: sortBy === opt.key ? '#007bff' : 'white',
                  color: sortBy === opt.key ? 'white' : '#555',
                }}
              >
                {opt.label}
              </button>
            ))}
          </div>

          <div style={{ display: 'flex', borderRadius: '6px', overflow: 'hidden', border: '1px solid #ccc', marginLeft: isMobile ? 'auto' : 0 }}>
            <button
              onClick={() => setViewMode('grid')}
              title="Grid view"
              style={{ padding: isMobile ? '8px 10px' : '10px 14px', border: 'none', cursor: 'pointer', fontSize: isMobile ? '0.85rem' : '1rem', backgroundColor: viewMode === 'grid' ? '#007bff' : 'white', color: viewMode === 'grid' ? 'white' : '#555' }}
            >▦</button>
            <button
              onClick={() => setViewMode('list')}
              title="List view"
              style={{ padding: isMobile ? '8px 10px' : '10px 14px', border: 'none', cursor: 'pointer', fontSize: isMobile ? '0.85rem' : '1rem', backgroundColor: viewMode === 'list' ? '#007bff' : 'white', color: viewMode === 'list' ? 'white' : '#555' }}
            >☰</button>
          </div>
        </div>

        {/* ===== POST-SCAN QUICK ACTION CARD (bottom sheet) ===== */}
        {scanHit && (
          <div
            style={{ position: 'fixed', inset: 0, zIndex: 9000, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'flex-end', justifyContent: 'center' }}
            onClick={() => setScanHit(null)}
          >
            <div
              onClick={(e) => e.stopPropagation()}
              style={{
                background: 'white', width: '100%', maxWidth: '480px',
                borderRadius: '16px 16px 0 0', padding: '20px',
                paddingBottom: 'calc(20px + env(safe-area-inset-bottom))',
                boxShadow: '0 -8px 30px rgba(0,0,0,0.3)',
              }}
            >
              {/* Tapping the item itself opens its full card (view/edit) */}
              <div
                onClick={() => { const item = scanHit; setScanHit(null); handleItemClick(item); }}
                style={{ display: 'flex', gap: '14px', alignItems: 'center', marginBottom: '16px', cursor: 'pointer' }}
              >
                <img
                  src={scanHit.image || 'https://placehold.co/100x100?text=No+Image'}
                  alt={scanHit.name}
                  style={{ width: '64px', height: '64px', borderRadius: '10px', objectFit: 'cover', flexShrink: 0 }}
                />
                <div style={{ minWidth: 0, flex: 1 }}>
                  <p style={{ margin: 0, fontWeight: 'bold', fontSize: '1.05rem' }}>{scanHit.name}</p>
                  <p style={{ margin: '2px 0 0', color: '#666', fontSize: '0.85rem' }}>
                    {scanHit.location || 'No location'} · <strong>{stockText(scanHit)}</strong> in stock
                  </p>
                  {Number(scanHit.price) > 0 && (
                    <p style={{ margin: '2px 0 0', color: '#888', fontSize: '0.8rem' }}>${Number(scanHit.price).toFixed(2)}</p>
                  )}
                </div>
                <span style={{ color: '#bbb', fontSize: '1.3rem', flexShrink: 0 }}>›</span>
              </div>

              {/* Signed counter: − builds a take (−5 = take 5), + builds a return */}
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '18px', marginBottom: '14px' }}>
                <button
                  onClick={() => setScanQty(Math.max(-scanHit.quantity, scanQty - 1))}
                  style={{ width: '56px', height: '56px', borderRadius: '12px', border: '1px solid #ccc', background: '#f5f5f5', fontSize: '1.7rem', cursor: 'pointer' }}
                >−</button>
                <span style={{
                  fontSize: '1.7rem', fontWeight: 'bold', minWidth: '72px', textAlign: 'center',
                  color: scanQty < 0 ? '#dc3545' : scanQty > 0 ? '#28a745' : '#999',
                }}>
                  {scanQty > 0 ? `+${scanQty}` : scanQty}
                </span>
                <button
                  onClick={() => setScanQty(scanQty + 1)}
                  style={{ width: '56px', height: '56px', borderRadius: '12px', border: '1px solid #ccc', background: '#f5f5f5', fontSize: '1.7rem', cursor: 'pointer' }}
                >+</button>
              </div>
              <p style={{ margin: '0 0 14px', textAlign: 'center', color: '#999', fontSize: '0.8rem' }}>
                {scanQty < 0 ? `Take ${Math.abs(scanQty)} from stock` : scanQty > 0 ? `Return ${scanQty} to stock` : 'Tap − to take · + to return'}
              </p>

              <button
                onClick={scanQuickSubmit}
                disabled={scanQty === 0}
                style={{
                  width: '100%', padding: '15px', borderRadius: '12px', border: 'none',
                  background: scanQty === 0 ? '#ddd' : scanQty < 0 ? '#dc3545' : '#28a745',
                  color: 'white', fontWeight: 'bold', fontSize: '1.05rem',
                  cursor: scanQty === 0 ? 'default' : 'pointer',
                }}
              >
                Submit
              </button>
              <p style={{ margin: '10px 0 0', textAlign: 'center', color: '#999', fontSize: '0.75rem' }}>
                Adds to your cart — confirm in Cart to update stock.
              </p>
            </div>
          </div>
        )}

        {/* Unified Popup (Add/Edit Item) — a fixed overlay so it opens right
            where you are (no more scrolling to the top of the page). On the
            phone it slides up as a bottom sheet filling the screen. */}
        {popupType !== 'none' && (
          <div
            style={{
              position: 'fixed', inset: 0, zIndex: 8000,
              background: 'rgba(0,0,0,0.5)',
              display: 'flex',
              alignItems: isMobile ? 'flex-end' : 'center',
              justifyContent: 'center',
            }}
            onClick={closePopup}
          >
          <div
            ref={popupRef}
            onClick={(e) => e.stopPropagation()}
            style={{
              backgroundColor: 'white',
              padding: '20px',
              paddingBottom: isMobile ? 'calc(20px + env(safe-area-inset-bottom))' : '20px',
              borderRadius: isMobile ? '16px 16px 0 0' : '14px',
              boxShadow: '0 8px 30px rgba(0,0,0,0.3)',
              width: '100%',
              maxWidth: isMobile ? '100%' : '560px',
              maxHeight: isMobile ? '92dvh' : '90vh',
              overflowY: 'auto',
              WebkitOverflowScrolling: 'touch',
              overscrollBehavior: 'contain', // don't hand scroll off to the page
            }}
          >
            {popupType === 'item' && selectedItem ? (
              <ItemPopup
                item={selectedItem}
                setSelectedItem={() => closePopup()}
                cart={cart}
                setCart={setCart}
                updateItem={updateItem}
                role={role}
                onDelete={handleDeleteItem}
              />
            ) : popupType === 'add' ? (
              <div>
                <h3 style={{ marginTop: 0, marginBottom: '16px' }}>Add New Item</h3>
                {Array.isArray(newItem.aliases) && newItem.aliases.length > 0 && (
                  <p style={{ marginTop: '-8px', marginBottom: '12px', fontSize: '0.85rem', color: '#28a745' }}>
                    📷 Scanned code <strong>{newItem.aliases[0]}</strong> will be linked to this item — future scans will find it automatically.
                  </p>
                )}
                <form onSubmit={handleCreateItem} style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                  <input
                    type="text"
                    placeholder="Item Name *"
                    value={newItem.name || ''}
                    onChange={(e) => setNewItem({ ...newItem, name: e.target.value })}
                    style={{ padding: '12px', borderRadius: '6px', border: '1px solid #ccc' }}
                    required
                  />
                  <LocationInput
                    value={newItem.location || ''}
                    onChange={(loc) => setNewItem({ ...newItem, location: loc })}
                    locations={recentLocations}
                    placeholder="Location (e.g., 1-A2) — recent spots suggested"
                    inputStyle={{ padding: '12px', borderRadius: '6px', border: '1px solid #ccc', fontSize: '1rem' }}
                  />
                  <input
                    type="number"
                    inputMode="numeric"
                    placeholder="Quantity"
                    value={newItem.quantity || ''}
                    onChange={(e) => setNewItem({ ...newItem, quantity: parseInt(e.target.value) || 0 })}
                    style={{ padding: '12px', borderRadius: '6px', border: '1px solid #ccc' }}
                    min="0"
                  />
                  <label style={{ display: 'flex', alignItems: 'center', gap: '10px', fontSize: '0.95rem', cursor: 'pointer' }}>
                    <input
                      type="checkbox"
                      checked={!!newItem.returnable}
                      onChange={(e) => setNewItem({ ...newItem, returnable: e.target.checked })}
                      style={{ width: '20px', height: '20px' }}
                    />
                    Returnable (tool — gets checked out and brought back)
                  </label>
                  <input
                    type="number"
                    inputMode="decimal"
                    placeholder="Price ($)"
                    step="0.01"
                    value={newItem.price || ''}
                    onChange={(e) => setNewItem({ ...newItem, price: parseFloat(e.target.value) || 0 })}
                    style={{ padding: '12px', borderRadius: '6px', border: '1px solid #ccc' }}
                    min="0"
                  />
                  <input
                    type="text"
                    placeholder="Slang names (comma-separated, e.g., pipe bender, hickey)"
                    value={newItem.aliasesText != null ? newItem.aliasesText : (Array.isArray(newItem.aliases) ? newItem.aliases.join(', ') : '')}
                    onChange={(e) => setNewItem({ ...newItem, aliasesText: e.target.value })}
                    style={{ padding: '12px', borderRadius: '6px', border: '1px solid #ccc' }}
                  />
                  <textarea
                    placeholder="Description (optional — specs, what it's for…)"
                    value={newItem.description || ''}
                    onChange={(e) => setNewItem({ ...newItem, description: e.target.value })}
                    rows={2}
                    style={{ padding: '12px', borderRadius: '6px', border: '1px solid #ccc', resize: 'vertical', fontFamily: 'inherit', fontSize: '1rem' }}
                  />
                  {/* Photo: paste a URL (shows instantly; the server saves a safe
                      copy in the background) or upload from this device. */}
                  <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
                    <input
                      type="text"
                      placeholder="Image URL (optional)"
                      value={newItem.image || ''}
                      onChange={(e) => setNewItem({ ...newItem, image: e.target.value })}
                      style={{ padding: '12px', borderRadius: '6px', border: '1px solid #ccc', flex: 1, minWidth: 0 }}
                    />
                    <button
                      type="button"
                      disabled={uploadingAddImg}
                      onClick={() => addFileRef.current && addFileRef.current.click()}
                      style={{ padding: '12px 14px', borderRadius: '6px', border: '1px solid #ccc', background: '#f8f9fa', cursor: 'pointer', whiteSpace: 'nowrap', fontWeight: 600 }}
                    >
                      {uploadingAddImg ? 'Uploading…' : '📷 Upload'}
                    </button>
                    <input
                      ref={addFileRef}
                      type="file"
                      accept="image/*"
                      style={{ display: 'none' }}
                      onChange={async (e) => {
                        const file = e.target.files && e.target.files[0];
                        e.target.value = '';
                        if (!file) return;
                        setUploadingAddImg(true);
                        try {
                          const fd = new FormData();
                          fd.append('image', file);
                          const res = await axios.post(`${API_BASE}/items/upload-image`, fd, {
                            headers: { Authorization: localStorage.getItem('token') },
                          });
                          setNewItem((prev) => ({ ...prev, image: res.data.url }));
                        } catch (err) {
                          console.error('Image upload failed:', err);
                          alert(err.response?.data?.error || 'Could not upload the image.');
                        } finally {
                          setUploadingAddImg(false);
                        }
                      }}
                    />
                  </div>
                  {newItem.image && (
                    <img
                      src={newItem.image}
                      alt="Preview"
                      style={{ width: '96px', height: '96px', objectFit: 'contain', border: '1px solid #ddd', borderRadius: '8px', backgroundColor: '#fff' }}
                    />
                  )}
                  <div style={{ display: 'flex', gap: '12px', marginTop: '12px' }}>
                    <button
                      type="submit"
                      disabled={isCreating}
                      style={{
                        padding: '12px 28px',
                        backgroundColor: isCreating ? '#94d3a2' : '#28a745',
                        color: 'white',
                        border: 'none',
                        borderRadius: '6px',
                        cursor: isCreating ? 'default' : 'pointer',
                        fontWeight: 'bold',
                      }}
                    >
                      {isCreating ? 'Creating…' : 'Create Item'}
                    </button>
                    <button
                      type="button"
                      onClick={closePopup}
                      style={{
                        padding: '12px 28px',
                        backgroundColor: '#6c757d',
                        color: 'white',
                        border: 'none',
                        borderRadius: '6px',
                        cursor: 'pointer',
                      }}
                    >
                      Cancel
                    </button>
                  </div>
                </form>
              </div>
            ) : null}
          </div>
          </div>
        )}

        {/* Items List */}
        {sortedItems.length === 0 ? (
          <p style={{ marginTop: '20px', color: '#666', fontSize: '1.1rem' }}>
            {searchTerm
              ? 'No items match your search.'
              : 'No items available yet.'}
            {['admin', 'superadmin'].includes(role) && !searchTerm && ' Try adding some!'}
          </p>
        ) : viewMode === 'grid' ? (
          /* ===== GRID VIEW — compact cards, ~3 across on a phone ===== */
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fill, minmax(110px, 1fr))',
              gap: '10px',
            }}
          >
            {sortedItems.slice(0, visibleCount).map((item) => (
              <div
                key={item._id}
                style={{
                  position: 'relative',
                  backgroundColor: 'white',
                  borderRadius: '10px',
                  border: selectMode && selectedIds.includes(item._id) ? '2px solid #007bff' : '1px solid #e5e5e5',
                  padding: selectMode && selectedIds.includes(item._id) ? '7px' : '8px',
                  cursor: 'pointer',
                  display: 'flex',
                  flexDirection: 'column',
                }}
                onClick={() => {
                  if (selectMode) { toggleSelected(item._id); return; }
                  if (deleteTargetId === item._id) { setDeleteTargetId(null); return; }
                  handleItemClick(item);
                }}
                onContextMenu={(e) => { if (isSuper && !selectMode) { e.preventDefault(); setDeleteTargetId(item._id); } }}
              >
                {/* Selection badge (select mode) */}
                {selectMode && (
                  <span style={{
                    position: 'absolute', top: '6px', right: '6px', zIndex: 4,
                    width: '24px', height: '24px', borderRadius: '50%',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    fontSize: '14px', fontWeight: 700,
                    background: selectedIds.includes(item._id) ? '#007bff' : 'rgba(255,255,255,0.9)',
                    color: selectedIds.includes(item._id) ? 'white' : '#bbb',
                    border: selectedIds.includes(item._id) ? 'none' : '2px solid #ccc',
                    boxShadow: '0 1px 4px rgba(0,0,0,0.2)',
                  }}>
                    ✓
                  </span>
                )}
                {isSuper && deleteTargetId === item._id && (
                  <button
                    onClick={(e) => { e.stopPropagation(); handleDeleteItem(item); }}
                    style={{
                      position: 'absolute', inset: 0, zIndex: 5,
                      backgroundColor: 'rgba(220,38,38,0.92)', color: 'white',
                      border: 'none', borderRadius: '10px', fontWeight: 700, cursor: 'pointer',
                    }}
                  >
                    🗑 Delete
                  </button>
                )}
                <div style={{ width: '100%', aspectRatio: '1', marginBottom: '6px' }}>
                  <ItemImg
                    src={itemImgSrc(item)}
                    alt={item.name}
                    style={{ width: '100%', height: '100%', objectFit: 'contain', borderRadius: '6px' }}
                  />
                </div>
                <p style={{
                  margin: 0, fontSize: '0.8rem', fontWeight: 600, lineHeight: 1.25,
                  display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical',
                  overflow: 'hidden', minHeight: '2.5em',
                }}>
                  {item.name}
                </p>
                <p style={{ margin: '4px 0 0', fontSize: '0.72rem', color: '#777' }}>
                  {item.location || '—'} · <strong style={{ color: '#333' }}>{stockText(item)}</strong>
                </p>
              </div>
            ))}
          </div>
        ) : (
          /* ===== LIST VIEW ===== */
          <div>
            {sortedItems.slice(0, visibleCount).map((item) => (
              <div
                key={item._id}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  borderBottom: '1px solid #eee',
                  padding: '16px 0',
                  cursor: 'pointer',
                  transition: 'background-color 0.15s',
                  position: 'relative',
                }}
                onClick={() => {
                  if (selectMode) { toggleSelected(item._id); return; }
                  if (deleteTargetId === item._id) { setDeleteTargetId(null); return; } // tap dismisses
                  handleItemClick(item);
                }}
                onContextMenu={(e) => { if (isSuper && !selectMode) { e.preventDefault(); setDeleteTargetId(item._id); } }}
                onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = '#f0f0f0')}
                onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = 'transparent')}
              >
                {/* Selection badge (select mode) */}
                {selectMode && (
                  <span style={{
                    marginRight: '12px', flexShrink: 0,
                    width: '26px', height: '26px', borderRadius: '50%',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    fontSize: '15px', fontWeight: 700,
                    background: selectedIds.includes(item._id) ? '#007bff' : 'white',
                    color: selectedIds.includes(item._id) ? 'white' : '#ccc',
                    border: selectedIds.includes(item._id) ? 'none' : '2px solid #ccc',
                  }}>
                    ✓
                  </span>
                )}
                {isSuper && deleteTargetId === item._id && (
                  <button
                    onClick={(e) => { e.stopPropagation(); handleDeleteItem(item); }}
                    style={{
                      position: 'absolute', right: 0, top: '50%', transform: 'translateY(-50%)',
                      zIndex: 5, padding: '10px 18px', backgroundColor: '#dc2626', color: 'white',
                      border: 'none', borderRadius: '8px', fontWeight: 700, cursor: 'pointer',
                      boxShadow: '0 2px 8px rgba(0,0,0,0.25)',
                    }}
                  >
                    🗑 Delete
                  </button>
                )}
                <div style={{ width: '100px', height: '100px', flexShrink: 0 }}>
                  <ItemImg
                    src={itemImgSrc(item)}
                    alt={item.name}
                    style={{ width: '100%', height: '100%', objectFit: 'contain', borderRadius: '4px' }}
                  />
                </div>
                <div style={{ flex: 1, paddingLeft: '16px' }}>
                  <h3 style={{ margin: '0 0 6px 0', fontSize: '1.25rem' }}>{item.name}</h3>
                  <p style={{ margin: '4px 0', color: '#555' }}>Location: {item.location || '—'}</p>
                  <p style={{ margin: '4px 0', color: '#555' }}>
                    Available: <strong>{stockText(item)}</strong>
                  </p>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Infinite-scroll sentinel: entering the viewport loads the next page. */}
        {sortedItems.length > visibleCount && (
          <div ref={sentinelRef} style={{ textAlign: 'center', padding: '24px 0', color: '#999', fontSize: '0.85rem' }}>
            Loading more…
          </div>
        )}

        {/* Spacer so the bulk-move bar never covers the last row */}
        {selectMode && <div style={{ height: '90px' }} />}
      </div>

      {/* Bulk-move bar: pinned to the bottom while selecting. Pick items, type
          (or pick) the new location, hit Move — every selected item updates. */}
      {selectMode && (
        <div style={{
          position: 'fixed', left: 0, right: 0, bottom: 0, zIndex: 7000,
          background: 'white', borderTop: '1px solid #ddd',
          boxShadow: '0 -4px 16px rgba(0,0,0,0.12)',
          padding: '12px 16px',
          paddingBottom: 'calc(12px + env(safe-area-inset-bottom))',
        }}>
          <div style={{ maxWidth: '640px', margin: '0 auto', display: 'flex', gap: '10px', alignItems: 'center' }}>
            <span style={{ fontWeight: 700, fontSize: '0.9rem', whiteSpace: 'nowrap', color: '#333' }}>
              {selectedIds.length} selected
            </span>
            <LocationInput
              value={bulkLoc}
              onChange={setBulkLoc}
              locations={recentLocations}
              placeholder="New location (e.g., 2-B2-2)"
              dropUp
              inputStyle={{ padding: '12px', borderRadius: '8px', border: '1px solid #ccc', fontSize: '1rem' }}
            />
            <button
              onClick={bulkMove}
              disabled={!selectedIds.length || !bulkLoc.trim() || bulkSaving}
              style={{
                padding: '12px 20px', borderRadius: '8px', border: 'none',
                fontWeight: 700, fontSize: '0.95rem', whiteSpace: 'nowrap',
                background: (!selectedIds.length || !bulkLoc.trim() || bulkSaving) ? '#bbb' : '#007bff',
                color: 'white',
                cursor: (!selectedIds.length || !bulkLoc.trim() || bulkSaving) ? 'default' : 'pointer',
              }}
            >
              {bulkSaving ? 'Moving…' : 'Move'}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

export default Inventory;